/**
 * React Hook for Aspect Ratio Detection
 * Provides a clean React interface for aspect ratio detection
 */

import { useState, useEffect, useCallback } from 'react';
import { MediaItem } from '../types';
import { 
  detectAspectRatio, 
  AspectRatioResult, 
  AspectRatioType, 
  DetectionOptions,
  getAspectRatioDisplay 
} from '../utils/aspectRatioDetector';

interface UseAspectRatioOptions extends DetectionOptions {
  autoDetect?: boolean;
  dependencies?: any[];
}

interface UseAspectRatioReturn {
  aspectRatio: AspectRatioType;
  result: AspectRatioResult | null;
  isDetecting: boolean;
  error: string | null;
  detect: (items: MediaItem[]) => Promise<void>;
  reset: () => void;
  displayInfo: {
    emoji: string;
    label: string;
    description: string;
  };
}

export const useAspectRatio = (
  mediaItems?: MediaItem[],
  options: UseAspectRatioOptions = {}
): UseAspectRatioReturn => {
  const {
    autoDetect = true,
    dependencies = [],
    ...detectionOptions
  } = options;

  const [aspectRatio, setAspectRatio] = useState<AspectRatioType>('16:9');
  const [result, setResult] = useState<AspectRatioResult | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialDetection, setHasInitialDetection] = useState(false); // Track if we've done first detection

  const detect = useCallback(async (items: MediaItem[]) => {
    setError(null);

    try {
      // Don't show loading for empty items - instant fallback
      if (!items || items.length === 0) {
        setResult(null);
        setAspectRatio(detectionOptions.fallbackRatio || '16:9');
        setIsDetecting(false);
        setHasInitialDetection(true);
        return;
      }

      // Always show loading state for media detection
      setIsDetecting(true);

      const detectionResult = await detectAspectRatio(items, detectionOptions);
      setResult(detectionResult);
      setAspectRatio(detectionResult.ratio);
      setHasInitialDetection(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Detection failed';
      setError(errorMessage);
      console.error('[useAspectRatio] Detection error:', err);
      setHasInitialDetection(true);
    } finally {
      setIsDetecting(false);
    }
  }, [detectionOptions]);

  const reset = useCallback(() => {
    setAspectRatio('16:9');
    setResult(null);
    setError(null);
    setIsDetecting(false);
    setHasInitialDetection(false);
  }, []);

  // Auto-detect when mediaItems change (only if autoDetect is enabled)
  useEffect(() => {
    if (autoDetect && mediaItems && mediaItems.length > 0) {
      detect(mediaItems);
    } else if (autoDetect && (!mediaItems || mediaItems.length === 0)) {
      reset();
    }
  }, [autoDetect, mediaItems, detect, reset, ...dependencies]);

  const displayInfo = getAspectRatioDisplay(aspectRatio);

  return {
    aspectRatio,
    result,
    isDetecting,
    error,
    detect,
    reset,
    displayInfo
  };
};
